export interface MatrixCallIdentity {
    readonly matrixCallId: string;
    readonly roomId: string;
    readonly remoteUserId?: string;
    readonly backendCallId?: string;
}

export type MatrixCallDirection = "incoming" | "outgoing";

export type MatrixCallMediaIntent = "audio" | "video";

export interface MatrixCallMediaState {
    readonly intent: MatrixCallMediaIntent;
    readonly localVideo: boolean;
    readonly remoteVideo: boolean;
    readonly localScreenShare: boolean;
    readonly remoteScreenShare: boolean;
}

export const initialCallMedia = (intent: MatrixCallMediaIntent): MatrixCallMediaState => ({
    intent,
    localVideo: intent === "video",
    remoteVideo: false,
    localScreenShare: false,
    remoteScreenShare: false,
});

export const callMediaOf = (state: MatrixCallSessionState): MatrixCallMediaState | undefined =>
    "media" in state ? state.media : undefined;

const sameMedia = (left: MatrixCallMediaState, right: MatrixCallMediaState): boolean =>
    left.intent === right.intent &&
    left.localVideo === right.localVideo &&
    left.remoteVideo === right.remoteVideo &&
    left.localScreenShare === right.localScreenShare &&
    left.remoteScreenShare === right.remoteScreenShare;

export type MatrixCallFailure =
    | "media_denied"
    | "signalling_failed"
    | "transport_failed"
    | "session_missing"
    | "unknown";

interface ActiveCallState {
    readonly call: MatrixCallIdentity;
    readonly direction: MatrixCallDirection;
    readonly startedAtMs: number;
    readonly media?: MatrixCallMediaState;
}

export type MatrixCallSessionState =
    | { readonly kind: "idle" }
    | (ActiveCallState & { readonly kind: "ringing" })
    | (ActiveCallState & {
          readonly kind: "connecting";
          readonly acceptedAtMs?: number;
      })
    | (ActiveCallState & {
          readonly kind: "connected";
          readonly connectedAtMs: number;
      })
    | (ActiveCallState & {
          readonly kind: "reconnecting";
          readonly connectedAtMs: number;
          readonly interruptedAtMs: number;
      })
    | {
          readonly kind: "ended";
          readonly call: MatrixCallIdentity;
          readonly endedAtMs: number;
          readonly reason: string;
      }
    | {
          readonly kind: "failed";
          readonly call?: MatrixCallIdentity;
          readonly failedAtMs: number;
          readonly failure: MatrixCallFailure;
          readonly detail?: string;
      };

export type MatrixCallSessionEvent =
    | (ActiveCallState & { readonly type: "incoming_received" })
    | (ActiveCallState & { readonly type: "outgoing_started" })
    | {
          readonly type: "local_accepted";
          readonly call: MatrixCallIdentity;
          readonly acceptedAtMs: number;
      }
    | {
          readonly type: "transport_connecting";
          readonly call: MatrixCallIdentity;
      }
    | {
          readonly type: "media_connected";
          readonly call: MatrixCallIdentity;
          readonly connectedAtMs: number;
      }
    | {
          readonly type: "transport_interrupted";
          readonly call: MatrixCallIdentity;
          readonly interruptedAtMs: number;
      }
    | {
          readonly type: "call_replaced";
          readonly call: MatrixCallIdentity;
          readonly replacement: MatrixCallIdentity;
      }
    | {
          readonly type: "media_changed";
          readonly call: MatrixCallIdentity;
          readonly media: Partial<MatrixCallMediaState>;
      }
    | {
          readonly type: "ended";
          readonly call: MatrixCallIdentity;
          readonly endedAtMs: number;
          readonly reason: string;
      }
    | {
          readonly type: "failed";
          readonly call?: MatrixCallIdentity;
          readonly failedAtMs: number;
          readonly failure: MatrixCallFailure;
          readonly detail?: string;
      }
    | { readonly type: "reset" };

export const isSameMatrixCall = (
    left: MatrixCallIdentity,
    right: MatrixCallIdentity,
): boolean =>
    left.matrixCallId === right.matrixCallId && left.roomId === right.roomId;

const isActiveState = (
    state: MatrixCallSessionState,
): state is Extract<MatrixCallSessionState, ActiveCallState> =>
    state.kind === "ringing" ||
    state.kind === "connecting" ||
    state.kind === "connected" ||
    state.kind === "reconnecting";

const canStartCall = (state: MatrixCallSessionState): boolean =>
    state.kind === "idle" || state.kind === "ended" || state.kind === "failed";

export const reduceMatrixCallSession = (
    state: MatrixCallSessionState,
    event: MatrixCallSessionEvent,
): MatrixCallSessionState => {
    switch (event.type) {
        case "reset":
            return { kind: "idle" };
        case "incoming_received":
            return canStartCall(state)
                ? {
                      kind: "ringing",
                      call: event.call,
                      direction: event.direction,
                      startedAtMs: event.startedAtMs,
                      media: event.media,
                  }
                : state;
        case "outgoing_started":
            return canStartCall(state)
                ? {
                      kind: "connecting",
                      call: event.call,
                      direction: event.direction,
                      startedAtMs: event.startedAtMs,
                      media: event.media,
                  }
                : state;
        case "local_accepted":
            if (
                state.kind !== "ringing" ||
                !isSameMatrixCall(state.call, event.call)
            ) {
                return state;
            }
            return {
                kind: "connecting",
                call: state.call,
                direction: state.direction,
                startedAtMs: state.startedAtMs,
                acceptedAtMs: event.acceptedAtMs,
                media: state.media,
            };
        case "transport_connecting":
            return isActiveState(state) && isSameMatrixCall(state.call, event.call)
                ? {
                      kind: "connecting",
                      call: state.call,
                      direction: state.direction,
                      startedAtMs: state.startedAtMs,
                      acceptedAtMs:
                          state.kind === "connecting"
                              ? state.acceptedAtMs
                              : undefined,
                      media: state.media,
                  }
                : state;
        case "media_connected":
            return isActiveState(state) && isSameMatrixCall(state.call, event.call)
                ? {
                      kind: "connected",
                      call: state.call,
                      direction: state.direction,
                      startedAtMs: state.startedAtMs,
                      connectedAtMs: event.connectedAtMs,
                      media: state.media,
                  }
                : state;
        case "transport_interrupted":
            if (
                (state.kind !== "connected" && state.kind !== "reconnecting") ||
                !isSameMatrixCall(state.call, event.call)
            ) {
                return state;
            }
            return {
                kind: "reconnecting",
                call: state.call,
                direction: state.direction,
                startedAtMs: state.startedAtMs,
                connectedAtMs: state.connectedAtMs,
                interruptedAtMs: event.interruptedAtMs,
                media: state.media,
            };
        case "call_replaced":
            return isActiveState(state) &&
                isSameMatrixCall(state.call, event.call) &&
                event.replacement.roomId === state.call.roomId
                ? {...state, call: event.replacement}
                : state;
        case "media_changed": {
            if (!isActiveState(state) || !isSameMatrixCall(state.call, event.call)) {
                return state;
            }
            const media = {...(state.media ?? initialCallMedia("audio")), ...event.media};
            return state.media && sameMedia(state.media, media) ? state : {...state, media};
        }
        case "ended":
            if (!isActiveState(state) || !isSameMatrixCall(state.call, event.call)) {
                return state;
            }
            return {
                kind: "ended",
                call: state.call,
                endedAtMs: event.endedAtMs,
                reason: event.reason,
            };
        case "failed":
            if (event.call) {
                if (!isActiveState(state) || !isSameMatrixCall(state.call, event.call)) {
                    return state;
                }
            } else if (!canStartCall(state)) {
                return state;
            }
            return {
                kind: "failed",
                call: event.call,
                failedAtMs: event.failedAtMs,
                failure: event.failure,
                detail: event.detail,
            };
        default:
            return assertNever(event);
    }
};

const assertNever = (value: never): never => {
    throw new Error(`Unhandled Matrix call event: ${String(value)}`);
};
